import {
  fetchAuditHistory,
  buildAuditHistoryPrintHtml,
  buildChangeSummaryPrintHtml,
  shouldShowAuditHistory
} from './form_audit_history_module.js'

export default class PrintFormModule {
  constructor({
    controller,
    formSchema,
    originalFormSchema = null, // Original unfiltered form schema (for printing without data)
    formResponses = null,
    stackId,
    studyId,
    dataSourceKey, // 'qc_details' or 'grading_details'
    endpointPath, // e.g., 'qc_forms/get_qc_form_details'
    formTitle, // 'QC FORM' or 'GRADING FORM'
    showModalityTitle = false,
    printFormWithData = false, // Flag to determine if form should print with data
    odOsOrderPreference = false, // Flag to determine if sections should be ordered by sect_number attribute
    renderFieldCallback = null, // Function to render field with/without data (optional, will use default if not provided)
    renderEmptyFieldCallback = null, // Optional callback for rendering empty fields (used when printFormWithData is false)
    shouldSkipComponentCallback = null, // Optional callback for filtering components (will use default if not provided)
    shouldSkipHtmlElementCallback = null, // Optional callback for QC's HTML element filtering
    shouldHideReasonImagesUngradableCallback = null, // Optional callback for grading/adjudication (will use default if not provided)
    shouldSkipReasonImagesUngradableCallback = null, // Optional callback for grading/adjudication (will use default if not provided)
    extractAnswerValueCallback = null, // Optional callback for grading/adjudication (will use default if not provided)
    defaultFontSize = null, // Font size for default renderField (null = auto-detect from formTitle)
    auditHistoryUrl = null,
    stackStatus = null,
    includeAuditSummaryInPdf = false,
    includeChangeSummaryInPdf = false,
  }) {
    this.controller = controller;
    this.formSchema = formSchema;
    this.originalFormSchema = originalFormSchema || formSchema; // Use original if provided, otherwise use formSchema
    this.formResponses = formResponses;
    this.stackId = stackId;
    this.studyId = studyId;
    this.dataSourceKey = dataSourceKey;
    this.endpointPath = endpointPath;
    this.formTitle = formTitle;
    this.showModalityTitle = showModalityTitle;
    this.printFormWithData = printFormWithData;
    this.odOsOrderPreference = odOsOrderPreference;
    this.renderFieldCallback = renderFieldCallback; // Will use default if null
    this.renderEmptyFieldCallback = renderEmptyFieldCallback;
    this.shouldSkipComponentCallback = shouldSkipComponentCallback || ((component) => this.shouldHideField(component));
    this.shouldSkipHtmlElementCallback = shouldSkipHtmlElementCallback;
    this.shouldHideReasonImagesUngradableCallback = shouldHideReasonImagesUngradableCallback || ((value) => this.defaultShouldHideReasonImagesUngradable(value));
    this.shouldSkipReasonImagesUngradableCallback = shouldSkipReasonImagesUngradableCallback || ((value) => this.defaultShouldSkipReasonImagesUngradable(value));
    this.extractAnswerValueCallback = extractAnswerValueCallback || ((value) => this.defaultExtractAnswerValue(value));
    // Determine font size: use provided, or auto-detect from formTitle, or default to 15px
    this.defaultFontSize = defaultFontSize || (this.formTitle === 'QC FORM' ? '18px' : '15px');
    this.auditHistoryUrl = auditHistoryUrl;
    this.stackStatus = stackStatus;
    this.includeAuditSummaryInPdf = includeAuditSummaryInPdf;
    this.includeChangeSummaryInPdf = includeChangeSummaryInPdf;
  }

  // Check if component should be hidden based on attributes.print_hide
  shouldHideField(component) {
    if (!component) return false;
    // Check if attributes.print_hide is "true"
    if (component.attributes && component.attributes.print_hide === "true") {
      return true;
    }
    return false;
  }

  // Check if section should be hidden based on attributes.section_hide
  shouldHideSection(component) {
    if (!component) return false;
    // Check if attributes.section_hide is present (any truthy value will hide the section)
    if (component.attributes && component.attributes.section_hide) {
      return true;
    }
    return false;
  }


  // Default implementation for extractAnswerValue (used by grading/adjudication)
  defaultExtractAnswerValue(value) {
    if (value === undefined || value === null) {
      return value;
    }

    if (typeof value === "object") {
      if (value.hasOwnProperty("value")) {
        return value.value;
      }

      const truthyKeys = Object.keys(value).filter(
        (key) => value[key] === true
      );

      if (truthyKeys.length === 1) {
        return truthyKeys[0];
      }
    }

    return value;
  }

  // Default implementation for shouldHideReasonImagesUngradable (used by grading/adjudication)
  defaultShouldHideReasonImagesUngradable(value) {
    if (value === undefined || value === null) {
      return false;
    }

    const answer = this.extractAnswerValueCallback(value);

    if (typeof answer === "boolean") {
      return answer === false;
    }

    if (typeof answer === "string") {
      const normalized = answer.trim().toLowerCase();
      if (normalized === "no") {
        return true;
      }
      if (normalized === "yes") {
        return false;
      }
    }

    return false;
  }

  // Default implementation for shouldSkipReasonImagesUngradable (used by grading/adjudication)
  defaultShouldSkipReasonImagesUngradable(value) {
    // Check controller state if available
    if (this.controller.reasonImagesUngradableHidden !== undefined && this.controller.reasonImagesUngradableHidden) {
      return true;
    }

    if (value === undefined || value === null || value === "") {
      return true;
    }

    if (typeof value === "object" && Object.keys(value).length === 0) {
      return true;
    }

    if (Array.isArray(value) && value.filter(Boolean).length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    return false;
  }

  // Default implementation for renderField (used by all controllers)
  defaultRenderField(subComponent, value) {
    // For grading/adjudication: check for "reason images are ungradable" at start
    // Use the default method directly to avoid circular reference
    if (
      subComponent.label &&
      subComponent.label.trim().toLowerCase() === "reason images are ungradable" &&
      this.defaultShouldSkipReasonImagesUngradable(value)
    ) {
      return "";
    }

    // For QC: if no value, this would typically call renderEmptyField, but we'll handle it here
    // (QC controller handles this separately in its renderField)

    if (subComponent.type === "radio" && Array.isArray(subComponent.values)) {
      // Render radio buttons with data
      return subComponent.values
        .map(
          (opt) => `
            <div style="margin-top: 5px; display: flex; align-items: center; gap: 8px;">
              <input type="radio" 
                id="${subComponent.key}_${opt.value}" 
                name="${subComponent.key}" 
                value="${opt.value}" 
                style="margin: 0;" 
                ${value == opt.value ? 'checked="checked"' : ""} 
                disabled="disabled" />
              <label for="${subComponent.key}_${opt.value}" 
                style="font-size: ${this.defaultFontSize}; font-family: Calibri, sans-serif; ${value == opt.value ? 'font-weight: bold; color: #333;' : ''}">
                ${(opt.label || "").toUpperCase()}
              </label>
            </div>
          `
        )
        .join("");
    } else if (subComponent.type === "selectboxes") {
      // Render selectboxes (Formio multi-select checkboxes) with data
      const options = subComponent.values || [];
      return options
        .map(
          (opt) => {
            const isChecked = value && value[opt.value] === true;
            return `
              <div style="margin-top: 5px; display: flex; align-items: center; gap: 8px;">
                <input type="checkbox" 
                  id="${subComponent.key}_${opt.value}" 
                  name="${subComponent.key}[${opt.value}]" 
                  value="${opt.value}" 
                  style="margin: 0;" 
                  ${isChecked ? 'checked="checked"' : ""} 
                  disabled="disabled" />
                <label for="${subComponent.key}_${opt.value}" 
                  style="font-size: ${this.defaultFontSize}; font-family: Calibri, sans-serif; ${isChecked ? 'font-weight: bold; color: #333;' : ''}">
                  ${(opt.label || "").toUpperCase()}
                </label>
              </div>
            `;
          }
        )
        .join("");
    } else if (subComponent.type === "checkbox" && Array.isArray(subComponent.values)) {
      // Render checkboxes with data
      return subComponent.values
        .map(
          (opt) => `
            <div style="margin-top: 5px; display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" 
                id="${subComponent.key}_${opt.value}" 
                name="${subComponent.key}[]" 
                value="${opt.value}" 
                style="margin: 0;" 
                ${value && value.includes(opt.value) ? 'checked="checked"' : ""} 
                disabled="disabled" />
              <label for="${subComponent.key}_${opt.value}" 
                style="font-size: ${this.defaultFontSize}; font-family: Calibri, sans-serif; ${value && value.includes(opt.value) ? 'font-weight: bold; color: #333;' : ''}">
                ${(opt.label || "").toUpperCase()}
              </label>
            </div>
          `
        )
        .join("");
    } else if (subComponent.type === "htmlelement") {
      return "";
    } else if (subComponent.type === "select") {
      // Render select dropdown with data
      const options = subComponent.data && subComponent.data.values 
        ? subComponent.data.values.map(opt => 
            `<option value="${opt.value}" ${value == opt.value ? 'selected' : ''}>${(opt.label || "").toUpperCase()}</option>`
          ).join('')
        : '';
      return `
        <div style="margin-bottom: 8px;">
          <select 
            id="${subComponent.key}" 
            name="${subComponent.key}" 
            style="width: 90%; font-size: ${this.defaultFontSize}; font-family: Calibri, sans-serif; padding: 5px; margin-top: 5px; ${value ? 'font-weight: bold; color: #333;' : ''}" 
            disabled="disabled">
            <option value="">Select...</option>
            ${options}
          </select>
        </div>
      `;
    } else if (subComponent.type === "textarea") {
      // Render textarea with fixed 4 rows for PDF
      return `
        <div style="margin-bottom: 8px;">
          <textarea 
            id="${subComponent.key}" 
            name="${subComponent.key}" 
            style="width: 90%; font-size: ${this.defaultFontSize}; font-family: Calibri, sans-serif; padding: 5px; margin-top: 5px; overflow: hidden; ${value ? 'border: 1px solid #333; font-weight: bold; color: #333;' : ''}" 
            disabled="disabled"
            rows="4">${value || ""}</textarea>
        </div>
      `;
    } else {
      // Render text input with data
      return `
        <div style="margin-bottom: 8px;">
          <input type="text" 
            id="${subComponent.key}" 
            name="${subComponent.key}" 
            value="${value || ""}" 
            style="width: 90%; font-size: ${this.defaultFontSize}; font-family: Calibri, sans-serif; padding: 5px; margin-top: 5px; ${value ? 'border: 1px solid #333; font-weight: bold; color: #333;' : ''}" 
            disabled="disabled" />
        </div>
      `;
    }
  }

  // Get form data from server
  getFormData() {
    const url = `${document.location.origin}/studies/${this.studyId}/${this.endpointPath}`;
    return fetch(url, {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'X-CSRF-Token': document.head.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      method: 'POST',
      body: JSON.stringify({
        stack_id: this.stackId
      })
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch form data');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching form details:', error);
        throw error;
      });
  }

  // Main print form method
  printForm() {
    // Debug: Log initial state
    console.log('PrintFormModule - printForm() called');
    console.log('PrintFormModule - printFormWithData:', this.printFormWithData);
    console.log('PrintFormModule - formResponses (initial):', this.formResponses);
    console.log('PrintFormModule - formResponses type:', typeof this.formResponses);
    if (this.formResponses && typeof this.formResponses === 'object') {
      console.log('PrintFormModule - formResponses keys:', Object.keys(this.formResponses));
      console.log('PrintFormModule - formResponses.data exists:', this.formResponses.data !== undefined);
      if (this.formResponses.data) {
        console.log('PrintFormModule - formResponses.data keys:', Object.keys(this.formResponses.data));
      }
    }
    
    const auditPromise = (
      this.printFormWithData &&
      (this.includeAuditSummaryInPdf || this.includeChangeSummaryInPdf) &&
      this.auditHistoryUrl &&
      shouldShowAuditHistory(this.stackStatus)
    ) ? fetchAuditHistory(this.auditHistoryUrl) : Promise.resolve(null)

    Promise.all([this.getFormData(), auditPromise])
      .then(([response, auditHistory]) => {
        const formDetails = response[this.dataSourceKey];
        const modalityName = formDetails.modality_name || "N/A";
        
        // If checkbox is not checked, set formResponses to null to show empty form (like grading)
        if (!this.printFormWithData) {
          this.formResponses = null;
          console.log('PrintFormModule - Checkbox not checked, set formResponses to null');
        } else {
          console.log('PrintFormModule - Checkbox checked, using formResponses:', this.formResponses);
        }
        
        // Set on controller if it exists (for grading/adjudication)
        if (this.controller.reasonImagesUngradableHidden !== undefined) {
          this.controller.reasonImagesUngradableHidden = false;
        }

        // Create the header table
        const customTable = this.createHeaderTable(formDetails);

        // Create the form table
        const formTable = this.createFormTable(formDetails);

        const studyName = formDetails.study_name;

        // Open a new window and add the tables
        const newWindow = window.open('', '_blank');
        
        if (!newWindow) {
          return;
        }

        // Add content to the new window
        newWindow.document.write(this.generatePrintHTML(studyName, modalityName));

        // Ensure the new window is ready before appending content
        newWindow.document.body.appendChild(customTable);
        newWindow.document.body.appendChild(formTable);

        if (this.includeChangeSummaryInPdf && auditHistory?.change_summary?.has_changes) {
          const changeSummarySection = document.createElement('div');
          changeSummarySection.innerHTML = buildChangeSummaryPrintHtml(auditHistory);
          if (changeSummarySection.innerHTML.trim()) {
            newWindow.document.body.appendChild(changeSummarySection);
          }
        }

        if (this.includeAuditSummaryInPdf && auditHistory?.versions?.length) {
          const auditSection = document.createElement('div');
          auditSection.innerHTML = buildAuditHistoryPrintHtml(auditHistory, document.location.origin);
          newWindow.document.body.appendChild(auditSection);
        }

        // Close the document to render content
        newWindow.document.close();
      })
      .catch(error => {
        console.error(error);
      });
  }

  // Create header table with form details
  createHeaderTable(formDetails) {
    const customTable = document.createElement('table');
    customTable.classList.add('custom-table');
    customTable.style.width = '100%';
    customTable.style.margin = '0 auto';
    customTable.style.borderCollapse = 'collapse';
    customTable.style.boxShadow = '0px 4px 8px rgba(0, 0, 0, 0.1)';
    customTable.style.borderRadius = '8px';
    customTable.style.overflow = 'hidden';
    customTable.style.marginBottom = '20px';
    customTable.style.fontFamily = 'Calibri, sans-serif';

    const headerDataPairs = [
      { header: 'Site', key: 'site_number' },
      { header: 'Subject ID', key: 'subject_code' },
      { header: 'Visit Name', key: 'visit_name' },
      { header: 'Visit Date', key: 'visit_date' },
    ];

    // Loop to create table rows for each header-data pair
    for (let i = 0; i < headerDataPairs.length; i += 4) {
      const row = customTable.insertRow();

      for (let j = i; j < i + 4 && j < headerDataPairs.length; j++) {
        const pair = headerDataPairs[j];
        const headerCell = row.insertCell();
        headerCell.textContent = pair.header;
        headerCell.style.fontWeight = 'bold';
        headerCell.style.padding = '12px';
        headerCell.style.borderBottom = '1px solid #ddd';

        const dataCell = row.insertCell();
        // If printFormWithData is false, show dashes instead of actual data
        dataCell.textContent = this.printFormWithData ? formDetails[pair.key] : '-';
        dataCell.style.padding = '12px';
        dataCell.style.borderBottom = '1px solid #ddd';
        dataCell.style.textAlign = this.formTitle === 'QC FORM' ? 'left' : 'center';
      }
    }

    return customTable;
  }

  // Sort components by sect_number or position attribute automatically if present
  sortComponentsBySectNumber(components) {
    // Check if any component has sect_number or position attribute
    const hasOrderingAttribute = components.some(component => {
      return (component.attributes?.sect_number || component.attributes?.position);
    });
    
    // If no component has ordering attribute, return original order
    if (!hasOrderingAttribute) {
      return components;
    }
    
    // Sort components by sect_number or position attribute
    return [...components].sort((a, b) => {
      // Try sect_number first, then position attribute
      const aOrder = a.attributes?.sect_number || a.attributes?.position;
      const bOrder = b.attributes?.sect_number || b.attributes?.position;
      
      const aOrderNum = aOrder ? parseInt(aOrder, 10) : Infinity;
      const bOrderNum = bOrder ? parseInt(bOrder, 10) : Infinity;
      
      // If both have ordering attribute, sort by it
      if (aOrderNum !== Infinity && bOrderNum !== Infinity) {
        return aOrderNum - bOrderNum;
      }
      
      // If only one has ordering attribute, prioritize it
      if (aOrderNum !== Infinity && bOrderNum === Infinity) {
        return -1;
      }
      if (aOrderNum === Infinity && bOrderNum !== Infinity) {
        return 1;
      }
      
      // If neither has ordering attribute, maintain original order
      return 0;
    });
  }

  // Create form table with components
  createFormTable(formDetails) {
    const formTable = document.createElement('table');
    formTable.classList.add('form-table');
    formTable.style.marginTop = '10px';
    formTable.style.width = '100%';
    formTable.style.margin = '0 auto';
    formTable.style.borderCollapse = 'collapse';
    formTable.style.boxShadow = '0px 4px 8px rgba(0, 0, 0, 0.1)';
    formTable.style.borderRadius = '8px';
    formTable.style.overflow = 'hidden';
    formTable.style.fontFamily = 'Calibri, sans-serif';
    formTable.style.fontSize = this.formTitle === 'QC FORM' ? '18px' : '15px';

    // When printing without data, use original unfiltered schema to show ALL sections
    // When printing with data, use filtered schema (current formSchema)
    const schemaToUse = !this.printFormWithData ? this.originalFormSchema : this.formSchema;
    
    // Sort components by sect_number if od_os_order_preference is enabled
    const sortedComponents = this.sortComponentsBySectNumber(schemaToUse.components);

    // Render form components
    sortedComponents.forEach((component) => {
      // Check if component should be skipped (e.g., Reason for Change)
      if (this.shouldSkipComponentCallback && this.shouldSkipComponentCallback(component)) {
        return;
      }

      // Check if entire section should be hidden based on attributes.section_hide
      if (this.shouldHideSection(component)) {
        return;
      }

      // Check if component should be hidden based on attributes.print_hide
      if (this.shouldHideField(component)) {
        return;
      }

      if (component.components && component.components.length > 0) {
        const visibleSubComponents = component.components.filter(
          (subComponent) => {
            // Skip Reason for Change if callback exists
            if (this.shouldSkipComponentCallback && this.shouldSkipComponentCallback(subComponent)) {
              return false;
            }
            // Skip if print_hide is true
            if (this.shouldHideField(subComponent)) {
              return false;
            }
            return true;
          }
        );

        if (visibleSubComponents.length === 0) {
          return;
        }

        // Create a full-width row for the component title
        const titleRow = formTable.insertRow();
        const titleCell = titleRow.insertCell();
        titleCell.colSpan = 2;
        titleCell.innerHTML = `
          <div style="font-weight: bold; font-size: 18px; margin: 10px 0; text-align: center; background: #eaf1dd; padding: 10px; border-radius: 5px; font-family: Calibri, sans-serif;">
            ${component.title || component.label || ''}
          </div>
        `;
        titleCell.style.textAlign = "center";
        titleCell.style.borderBottom = "2px solid #ddd";

        // Render subcomponents
        visibleSubComponents.forEach((subComponent) => {
          if (subComponent.type === "htmlelement") {
            // Check if HTML element should be skipped (QC specific)
            if (this.shouldSkipHtmlElementCallback && this.shouldSkipHtmlElementCallback(subComponent)) {
              return;
            }
            // Add a single row with full-width content for htmlelement
            const htmlRow = formTable.insertRow();
            const htmlCell = htmlRow.insertCell();
            htmlCell.colSpan = 2;
            const fontSize = this.formTitle === 'QC FORM' ? '16px' : '14px';
            htmlCell.innerHTML = `
              <div style="font-weight: bold; font-size: ${fontSize}; margin: 5px 0; text-align: center; background: #eaf1dd; padding: 2px; border-radius: 5px; line-height: 1.0; font-family: Calibri, sans-serif;">
                ${subComponent.content}
              </div>
            `;
            htmlCell.style.textAlign = "center";
            htmlCell.style.borderBottom = "1px solid #ddd";
          } else {
            // Add a row for the question
            const row = formTable.insertRow();

            // First column: Question
            const questionCell = row.insertCell();
            const description = subComponent.description
              ? `<div style="font-size: ${this.formTitle === 'QC FORM' ? '14px' : '12px'}; color: #666; margin-top: 5px; font-family: Calibri, sans-serif;">${subComponent.description}</div>`
              : "";
            const questionFontSize = this.formTitle === 'QC FORM' ? '18px' : '15px';
            questionCell.innerHTML = `
              <div style="font-weight: bold; font-size: ${questionFontSize}; margin-bottom: 5px; font-family: Calibri, sans-serif;">
                ${subComponent.label}
              </div>
              ${description}
            `;
            questionCell.style.textAlign = "left";
            questionCell.style.borderBottom = "1px solid #ddd";
            questionCell.style.padding = "8px";
            questionCell.style.width = "50%";
            questionCell.style.verticalAlign = "top";

            // Second column: Answer field
            const answerCell = row.insertCell();
            let fieldValue = null;
            
            // Get field value if form responses exist AND printFormWithData is true
            if (this.printFormWithData && this.formResponses) {
              // Handle both structures: formResponses.data or formResponses['data']
              // Also handle case where formResponses itself is the data object
              let responseData = null;
              
              // Check for standard structure: {data: {...}, sdv: {...}, files: {...}}
              // This is the structure used by QC forms
              if (this.formResponses.data !== undefined) {
                responseData = this.formResponses.data;
              } else if (this.formResponses['data'] !== undefined) {
                responseData = this.formResponses['data'];
              } else if (typeof this.formResponses === 'object' && !Array.isArray(this.formResponses) && this.formResponses !== null) {
                // If formResponses itself is an object (not nested), use it directly
                // This handles cases where responses are passed directly without a 'data' wrapper
                responseData = this.formResponses;
              }
              
              // Debug: Log first field extraction attempt
              if (!this._firstFieldLogged && subComponent.key) {
                this._firstFieldLogged = true;
                console.log(`PrintFormModule - Extracting field "${subComponent.key}":`, {
                  formResponses: this.formResponses,
                  responseData: responseData,
                  hasDataKey: this.formResponses.data !== undefined,
                  responseDataKeys: responseData && typeof responseData === 'object' ? Object.keys(responseData) : 'N/A'
                });
              }
              
              // If we found responseData and it's an object with keys, get the field value
              if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
                fieldValue = responseData[subComponent.key];
                if (!this._firstFieldLogged && fieldValue !== undefined && fieldValue !== null) {
                  console.log(`PrintFormModule - Found value for "${subComponent.key}":`, fieldValue);
                }
              } else if (this.printFormWithData && subComponent.key) {
                // Debug: Log when we can't find the field value (only for first few fields to avoid spam)
                if (!this._debugLoggedFields) {
                  this._debugLoggedFields = new Set();
                }
                if (this._debugLoggedFields.size < 3 && !this._debugLoggedFields.has(subComponent.key)) {
                  this._debugLoggedFields.add(subComponent.key);
                  console.warn(`PrintFormModule - Field "${subComponent.key}": Could not extract value. formResponses:`, this.formResponses, 'responseData:', responseData);
                }
              }
            } else if (this.printFormWithData && !this.formResponses) {
              // Debug: Log when checkbox is checked but formResponses is null/undefined
              if (!this._noResponsesLogged) {
                this._noResponsesLogged = true;
                console.warn('PrintFormModule - Checkbox is checked but formResponses is null/undefined!');
              }
            }

            // Only apply conditional filtering logic when printFormWithData is true
            // When printing without data, show ALL sections and questions
            if (this.printFormWithData) {
              // Check for "reason images are ungradable" filtering (grading/adjudication)
              if (
                subComponent.label &&
                subComponent.label.trim().toLowerCase() === "reason images are ungradable"
              ) {
                if (this.shouldHideReasonImagesUngradableCallback && 
                    this.shouldHideReasonImagesUngradableCallback()) {
                  formTable.deleteRow(formTable.rows.length - 1);
                  return;
                }
              }

              // Handle "reason images are ungradable" logic for grading/adjudication
              const normalizedLabel = (subComponent.label || "").trim().toLowerCase();
              if (normalizedLabel === "are the images gradable?") {
                if (this.shouldHideReasonImagesUngradableCallback) {
                  const hidden = this.shouldHideReasonImagesUngradableCallback(fieldValue);
                  // Set on controller if it exists
                  if (this.controller.reasonImagesUngradableHidden !== undefined) {
                    this.controller.reasonImagesUngradableHidden = hidden;
                  }
                }
              }
              if (normalizedLabel === "reason images are ungradable" && 
                  this.shouldSkipReasonImagesUngradableCallback && 
                  this.shouldSkipReasonImagesUngradableCallback(fieldValue)) {
                formTable.deleteRow(formTable.rows.length - 1);
                return;
              }
            }

            // Render the field - use renderFieldCallback if printFormWithData is true, otherwise show full form empty
            if (this.printFormWithData) {
              // When printing with data, use renderFieldCallback (it will handle null/undefined values)
              const renderFn = this.renderFieldCallback || ((subComponent, value) => this.defaultRenderField(subComponent, value));
              answerCell.innerHTML = renderFn(subComponent, fieldValue);
            } else {
              // When printing without data, show full form structure but empty (like grading)
              // Use defaultRenderField with null value to show all options/fields but unselected/empty
              answerCell.innerHTML = this.defaultRenderField(subComponent, null);
            }
            answerCell.style.textAlign = "left";
            answerCell.style.borderBottom = "1px solid #ddd";
            answerCell.style.padding = "8px";
            answerCell.style.width = "50%";
            answerCell.style.verticalAlign = "top";
          }
        });
      }
    });

    return formTable;
  }

  // Generate HTML for print window
  generatePrintHTML(studyName, modalityName) {
    const modalityTitle = this.showModalityTitle && modalityName !== "N/A"
      ? `<div class="modality-title">${modalityName} - ${this.formTitle === 'QC FORM' ? 'Quality Control Form' : 'Grading Form'}</div>`
      : (this.formTitle === 'GRADING FORM' && !this.showModalityTitle
          ? `<div class="custom-header">${this.formTitle}</div>`
          : '');

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${this.formTitle}</title>
        <style>
          ${this.getPrintStyles()}
        </style>
      </head>
      <body>
        <div class="header-container">
          <div class="left-text">OCULAR IMAGING <br> RESEARCH & <br> READING CENTER</div>
          <div class="study-title">${studyName}</div>
          <img src="${document.querySelector('meta[name="asset-base-path"]').getAttribute('content')}" alt="Logo" class="logo" />
        </div>
        ${modalityTitle}
      </body>
      </html>
    `;
  }

  // Get print-specific CSS styles
  getPrintStyles() {
    const baseStyles = `
      @page {
        margin-top: 0.5in;
        margin-bottom: 0.5in;
        margin-left: 0.5in;
        margin-right: 0.5in;
      }
      @media print {
        @page {
          margin-top: 0.5in;
          margin-bottom: 0.5in;
          margin-left: 0.5in;
          margin-right: 0.5in;
        }
      }
      .custom-table, .form-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 20px;
      }
      .custom-table th, .custom-table td,
      .form-table th, .form-table td {
        border: 1px solid #ddd;
        padding: 2px;
        text-align: center;
      }
      .custom-header {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 20px;
        text-align: center;
        color: #17365d;
        width: 100%;
        font-family: Calibri, sans-serif;
      }
      .modality-title {
        font-size: ${this.formTitle === 'QC FORM' ? '22px' : '18px'};
        font-weight: bold;
        margin-bottom: 16px;
        text-align: center;
        color: #17365d;
        ${this.formTitle === 'QC FORM' ? 'margin-left: -70px;' : ''}
        font-family: Calibri, sans-serif;
      }
      .header-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;
        position: relative;
        padding: 0 20px;
        box-sizing: border-box;
      }
      .left-text {
        font-size: ${this.formTitle === 'QC FORM' ? '22px' : '18px'};
        color: #17365d;
        font-weight: bold;
        font-family: Calibri, sans-serif;
        flex: 0 0 auto;
        min-width: 200px;
        max-width: 25%;
        padding-right: 15px;
        line-height: 1.3;
        white-space: normal;
      }
      .study-title {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        font-size: ${this.formTitle === 'QC FORM' ? '28px' : '22px'};
        font-weight: bold;
        text-align: center;
        color: #17365d;
        font-family: Calibri, sans-serif;
        max-width: 55%;
        line-height: 1.3;
        padding: 0 15px;
        box-sizing: border-box;
        word-wrap: normal;
        overflow-wrap: normal;
        white-space: normal;
      }
      .logo {
        width: 200px;
        height: auto;
        flex: 0 0 auto;
        margin-left: auto;
      }
      .audit-history-print {
        page-break-before: auto;
      }
      .audit-history-print img.audit-history-signature {
        max-height: 70px;
        width: auto;
      }
      @media print {
        .header-container {
          padding: 0 15px;
        }
        .study-title {
          max-width: 50%;
          font-size: ${this.formTitle === 'QC FORM' ? '19px' : '16px'};
          padding: 0 25px;
          line-height: 1.3;
        }
        .logo {
          width: 160px;
        }
        .left-text {
          font-size: ${this.formTitle === 'QC FORM' ? '18px' : '15px'};
          min-width: 180px;
          max-width: 22%;
          padding-right: 20px;
          line-height: 1.3;
        }
      }
    `;

    // Add QC-specific styles
    if (this.formTitle === 'QC FORM') {
      return baseStyles + `
        .custom-header {
          margin-left: -70px;
        }
      `;
    }

    return baseStyles;
  }
}

